import React from "react";
import { ArrowRight, ExternalLink } from "lucide-react";
import {
  buildPrecedentDetailUrl,
  buildPrecedentSearchUrl,
} from "./precedentNavigation";

export const OFFICIAL_AMENDMENT_SEARCH_URL =
  "https://www.law.go.kr/LSW/admRulSc.do?eventGubun=060103&menuId=5&query=%EC%88%98%EC%B6%9C%EC%9E%85%EB%AC%BC%ED%92%88+%EB%93%B1%EC%97%90+%EB%8C%80%ED%95%9C+%ED%92%88%EB%AA%A9%EB%B6%84%EB%A5%98+%EB%B3%80%EA%B2%BD%EA%B3%A0%EC%8B%9C&subMenuId=45&tabMenuId=183";

// 현행 제2026-38호의 국가법령정보센터 전체 행정규칙 화면에서 별표/서식
// 항목으로 이동한다. admRulBylContentsInfoR.do는 사이트 내부 프레임용 원시
// 이미지라 직접 열면 축소된 페이지만 보이므로 사용자 링크로 노출하지 않는다.
export const OFFICIAL_AMENDMENT_NOTICE_URL =
  `${OFFICIAL_AMENDMENT_SEARCH_URL}#J3239155`;

const RETIRED_AMENDMENT_NOTICE_URL = /admRulLsInfoP\.do\?admRulId=36850(?:&|$)/i;
const RAW_AMENDMENT_ATTACHMENT_URL = /admRulBylContentsInfoR\.do\?bylSeq=3239155(?:&|$)/i;

function validAmendmentLandingUrl(value) {
  const url = String(value || "");
  if (
    !/^https?:\/\//i.test(url)
    || RETIRED_AMENDMENT_NOTICE_URL.test(url)
    || RAW_AMENDMENT_ATTACHMENT_URL.test(url)
  ) {
    return OFFICIAL_AMENDMENT_NOTICE_URL;
  }
  // 기존 DB 행은 현행 검색 URL 또는 위치 앵커를 보유한다. 둘 다 전체 행정규칙
  // 화면의 별표/서식 위치로 통일한다.
  if (url === OFFICIAL_AMENDMENT_SEARCH_URL || url === `${OFFICIAL_AMENDMENT_SEARCH_URL}#J3239155`) {
    return OFFICIAL_AMENDMENT_NOTICE_URL;
  }
  return url;
}

/** 변경고시 게시 페이지와 그 페이지에 첨부된 공식 PDF를 서로 다른 근거로 표시한다. */
export function amendmentEvidence({ sourceLandingUrl, sourceUrl, sourcePage, seqNo } = {}) {
  const landingUrl = validAmendmentLandingUrl(sourceLandingUrl);
  const pdfUrl = /^https?:\/\//i.test(String(sourceUrl || "")) ? sourceUrl : null;
  const hasSeparatePdf = pdfUrl && pdfUrl !== landingUrl;
  return {
    landing: {
      url: landingUrl,
      label: "국가법령정보센터 별표·서식",
      title: seqNo
        ? `변경고시 연번 ${seqNo}의 국가법령정보센터 별표·서식 화면`
        : "품목분류 변경고시 국가법령정보센터 별표·서식 화면",
    },
    pdf: hasSeparatePdf
      ? {
          url: pdfUrl,
          label: sourcePage ? `공식 PDF ${sourcePage}쪽` : "공식 PDF",
          title: seqNo
            ? `변경고시 연번 ${seqNo} 공식 PDF${sourcePage ? ` ${sourcePage}쪽` : ""}`
            : `품목분류 변경고시 공식 PDF${sourcePage ? ` ${sourcePage}쪽` : ""}`,
        }
      : null,
  };
}

/** PDF 파싱 전문에서 실제 변경 판단 부분만 남기고 공통 시행 문구·후속 표를 제거한다. */
export function amendmentReasonText(reason) {
  const raw = String(reason || "").trim();
  if (!raw) return "";
  return raw
    .replace(/^(?:○\s*)?변경사유\s*(?:[-–—:：]\s*)?/, "")
    .split(/\(\s*(?:시행일|적용일)\s*\)/)[0]
    .replace(/\s*-\s*\d+\s*-\s*$/, "")
    .trim();
}

export const OFFICIAL_DOMESTIC_PRECEDENT_SEARCH_URL =
  "https://unipass.customs.go.kr/clip/prlstclsfsrch/openULS0203002S.do";

export const OFFICIAL_FOREIGN_PRECEDENT_SEARCH_URL =
  "https://unipass.customs.go.kr/clip/prlstclsfsrch/openULS0203048S.do";

export function tariffEvidenceUrl(code) {
  const searchVal = String(code || "").replace(/[^0-9]/g, "");
  if (!searchVal) return null;
  return `https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201007Q.do?searchVal=${encodeURIComponent(searchVal)}`;
}

/**
 * 품목분류사례의 공식 근거 주소와 표시 문구를 함께 만든다.
 *
 * CLIP 국내사례 case_id의 숫자부는 관세청 상세 팝업의 rrdcNo와 같다.
 * 그 밖의 자료는 임의의 상세주소를 추측하지 않고 공식 사례 검색 화면으로 보낸다.
 */
export function precedentEvidence({
  decisionUrl,
  caseId,
  source,
  code,
  refNo,
  officialForeignSearch = false,
  officialUrl,
} = {}) {
  const explicitUrl = String(decisionUrl || "").trim();
  const genericForeignSearchUrl =
    source === "clip_foreign"
    && /^https?:\/\/unipass\.customs\.go\.kr\/clip\/prlstclsfsrch\/openULS0203048S\.do(?:[?#].*)?$/i.test(explicitUrl);
  if (/^https?:\/\//i.test(explicitUrl) && !genericForeignSearchUrl) {
    // 위원회·협의회 결정은 국내사례와 상세 주소가 달라 적재 때 decision_url 로 싣는다(문의 #71).
    const officialDomestic =
      source === "clip_kcs" && /^https?:\/\/unipass\.customs\.go\.kr\//i.test(explicitUrl);
    return {
      url: explicitUrl,
      label: officialDomestic ? "관세청 결정 원문" : "결정 원문",
      title: `${refNo || caseId || "품목분류 결정"} 원문 새 창에서 열기`,
    };
  }

  const clipMatch = /^CLIP-(\d{8,20})$/.exec(String(caseId || "").trim());
  if (source === "clip_kcs" && clipMatch) {
    return {
      url: `https://unipass.customs.go.kr/clip/prlstclsfsrch/openULS0203003S.do?rrdcNo=${encodeURIComponent(clipMatch[1])}`,
      label: "관세청 결정 원문",
      title: `${refNo || caseId} 관세청 결정 원문 새 창에서 열기`,
    };
  }

  if (["clip_kcs", "kcs_pre_ruling", "bigdata_customs"].includes(source)) {
    return {
      url: OFFICIAL_DOMESTIC_PRECEDENT_SEARCH_URL,
      label: "관세청 사례 검색",
      title: `${refNo || caseId || "표시된 참조번호"}로 관세청 국내사례에서 확인`,
    };
  }

  if (source === "clip_foreign") {
    // 상세 API가 만든 검색 조건(국가·참조번호 또는 시행일) 포함 관세청 화면. 사례 개별 주소는 관세청이
    // 제공하지 않으므로 목록의 행을 누르면 상세가 열린다.
    if (officialForeignSearch && /^https?:\/\/unipass\.customs\.go\.kr\/clip\/prlstclsfsrch\/openULS0203048S\.do\?.*(?:reffNo|stDt)=/i.test(String(officialUrl || ""))) {
      return {
        url: String(officialUrl),
        label: "관세청 해외사례 원문 화면",
        title: `${refNo || caseId || "이 사례"} 검색 조건이 채워진 관세청 화면 — 목록의 행을 누르면 원문 상세가 열립니다`,
      };
    }
    if (!officialForeignSearch) {
      const query = String(code || refNo || "").trim();
      const internalUrl = caseId
        ? buildPrecedentDetailUrl({ caseId, query, source: "foreign" })
        : query
          ? buildPrecedentSearchUrl({ query, source: "foreign" })
          : null;
      if (internalUrl) {
        return {
          url: internalUrl,
          label: caseId ? "LoginHS 해외사례 상세" : "LoginHS 해외사례 검색",
          title: `${refNo || caseId || query} LoginHS 보유 해외사례에서 확인`,
        };
      }
    }
    return {
      url: OFFICIAL_FOREIGN_PRECEDENT_SEARCH_URL,
      label: "관세청 해외사례 검색 화면",
      title: "관세청 해외사례 검색 화면 — 검색조건을 직접 입력해야 합니다",
    };
  }

  return {
    url: tariffEvidenceUrl(code),
    label: "관세청 세번 조회",
    title: "표시된 세번을 관세청에서 확인",
  };
}

/** 원문 확인 동작을 결과 행의 다른 이동과 분리하는 공통 에비던스 버튼. */
export default function SourceEvidenceLink({
  url,
  label = "원문 근거",
  title,
  compact = false,
  iconOnly = false,
}) {
  if (!url) return null;
  const internal = /^\/(?!\/)/.test(String(url));
  const accessibleLabel = internal
    ? `${label}로 이동`
    : `${label} 새 창에서 열기`;
  const LinkIcon = internal ? ArrowRight : ExternalLink;
  return (
    <a
      href={url}
      target={internal ? undefined : "_blank"}
      rel={internal ? undefined : "noreferrer"}
      title={title || accessibleLabel}
      aria-label={accessibleLabel}
      onClick={(event) => {
        event.stopPropagation();
        if (
          internal
          && event.button === 0
          && !event.metaKey
          && !event.ctrlKey
          && !event.shiftKey
          && !event.altKey
        ) {
          event.preventDefault();
          window.history.pushState({}, "", url);
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: iconOnly ? 0 : 4,
        minHeight: compact ? 24 : 28,
        minWidth: iconOnly ? (compact ? 24 : 28) : undefined,
        padding: iconOnly ? 0 : compact ? "2px 7px" : "4px 9px",
        border: "1px solid var(--audit-border-strong, var(--audit-border))",
        borderRadius: 5,
        background: "var(--audit-bg-elevated, var(--audit-bg))",
        color: "var(--audit-accent)",
        fontSize: "var(--audit-type-caption-size, 12px)",
        fontWeight: 650,
        lineHeight: 1.2,
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      {!iconOnly && label}
      <LinkIcon size={iconOnly ? 14 : compact ? 12 : 13} aria-hidden="true" />
    </a>
  );
}
